import { ReactElement } from "react";
import { IPurchaseCardProps } from "../types";
import { Flex } from "@mantine/core";
import {
  getPrettyPlanName,
  isCanceledOrDeleted,
} from "@stores/purchases/utils";
import DisabledContainer from "@common/components/layout/DisabledContainer";
import AccordionCustom from "@common/components/thirdParty/mantine/accordion/AccordionCustom";
import { getPurchaseCardId } from "../utils";
import AccordionHeader from "@common/components/thirdParty/mantine/accordion/AccordionHeader";
import TextWithIcon from "@common/components/thirdParty/mantine/text/TextWithIcon";
import IconUsers from "@common/components/icons/IconUsers";
import { pluralize } from "@common/utils/string";
import CanceledSubscriptionSection from "../section/CanceledSubscriptionSection";
import SubscriptionSection from "../section/SubscriptionSection";
import { SUBSCRIPTION_STATUS_CANCELLATION_PENDING } from "@stores/subscriptions/constants";
import CancelSubscriptionSection from "../section/CancelSubscriptionSection";
import { STATUS_CHANGE_REGION } from "@stores/instances/constants";
import {
  formatInstanceVersion,
  getInstancePathname,
  getInstanceUrl,
} from "@stores/instances/utils";
import { useReloadInstanceWhenChanging } from "@hooks/useReloadInstanceWhenChanging";
import HealthStatus from "@common/components/ui/health/HealthStatus";
import IconComputer from "@common/components/icons/IconComputer";
import IconGlobe from "@common/components/icons/IconGlobe";
import TokenSubscriptionSection from "../section/TokenSubscriptionSection";

export default function UnsupportedPurchaseCard({
  purchase,
  account,
  paymentMethod,
  defaultSection,
}: IPurchaseCardProps): ReactElement {
  const { id, subscription, hosted_instance, user_count, token } = purchase;
  const { health, alias_url, dns_alias, status, mb_version, region } =
    hosted_instance || {};
  const instanceHealth =
    status === STATUS_CHANGE_REGION ? STATUS_CHANGE_REGION : health;

  const isPurchaseCanceledOrDeleted = isCanceledOrDeleted(purchase);

  const instanceUrl =
    alias_url && dns_alias
      ? getInstanceUrl({ alias_url, dns_alias })
      : undefined;

  const { isInstanceChanging } = useReloadInstanceWhenChanging(purchase);

  return (
    <DisabledContainer
      key={id}
      disabled={isPurchaseCanceledOrDeleted}
      data-testid="PurchaseCard"
    >
      <AccordionCustom
        id={getPurchaseCardId(purchase)}
        sections={[
          `pro-trial-${id}`,
          `dns-alias-${id}`,
          `hosting-region-${id}`,
          `custom-domain-${id}`,
          `migrate-to-cloud-${id}`,
        ]}
        defaultSection={defaultSection}
      >
        <AccordionHeader
          data-testid="PurchaseCard_header"
          title={
            alias_url && dns_alias
              ? getInstancePathname({ alias_url, dns_alias })
              : getPrettyPlanName(purchase)
          }
          titleLink={isPurchaseCanceledOrDeleted ? undefined : instanceUrl}
          externalLink={!isPurchaseCanceledOrDeleted}
          progress={isInstanceChanging}
        >
          <Flex
            align={{ base: "flex-start", xs: "center" }}
            direction={{ base: "column", xs: "row" }}
            gap={{ base: "xs", xs: "md" }}
          >
            {!isPurchaseCanceledOrDeleted && instanceHealth && (
              <HealthStatus
                health={instanceHealth}
                displayHealth
                mb="0"
                mt="0"
              />
            )}
            {mb_version && (
              <TextWithIcon
                icon={<IconComputer />}
                text={formatInstanceVersion(mb_version)}
                tooltip="Instance's Metabase version"
              />
            )}
            {!isPurchaseCanceledOrDeleted && (
              <>
                {status !== STATUS_CHANGE_REGION && region && (
                  <TextWithIcon
                    icon={<IconGlobe size={12} />}
                    text={region}
                    tooltip="Instance's Metabase region"
                  />
                )}
                {typeof user_count === "number" && user_count > 0 && (
                  <TextWithIcon
                    icon={<IconUsers />}
                    text={`${user_count} ${pluralize("users", user_count)}`}
                  />
                )}
              </>
            )}
          </Flex>
        </AccordionHeader>

        {token && <TokenSubscriptionSection purchase={purchase} />}

        {isPurchaseCanceledOrDeleted ? (
          <CanceledSubscriptionSection purchase={purchase} />
        ) : (
          <>
            <SubscriptionSection
              purchase={purchase}
              account={account}
              paymentMethod={paymentMethod}
            />

            {subscription.status !==
              SUBSCRIPTION_STATUS_CANCELLATION_PENDING && (
              <CancelSubscriptionSection purchase={purchase} />
            )}
          </>
        )}
      </AccordionCustom>
    </DisabledContainer>
  );
}
